import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { memo, ReactNode, useEffect, useRef } from "react";
import {
  ButtonSize,
  RedoButtonCloseX,
} from "../arbiter-components/redo-button-close-x";
import ImageIcon from "../arbiter-icon/image-01.svg";
import { Flex } from "../flex";
import * as signupFormCss from "./signup-form.module.css";

export const SignupFormContainer = memo(function SignupFormContainer({
  form,
  children,
  onClose,
  previewMobile,
}: {
  form: SignupForm;
  children: ReactNode;
  onClose?: () => void;
  previewMobile: boolean;
}) {
  const showMobileView = useScreenType() === ScreenType.MOBILE || previewMobile;
  const signupFormWidth = form.layout.type === "popup" ? "600px" : "100%";
  const signupFormMobileWidth =
    form.layout.type === "popup" ? "375px" : "430px";
  const signupformHeight = form.layout.type === "popup" ? "auto" : "100%";
  const size = {
    width: showMobileView ? signupFormMobileWidth : signupFormWidth,
    height: signupformHeight,
    borderRadius: form.theme.border.cornerRadiusPx,
  };
  const style = {
    backgroundColor: form.layout.backgroundColor,
    border: `${form.theme.border.stroke.weightPx}px solid ${form.theme.border.stroke.color}`,
  };
  const closeButtonStyle = {
    color: form.theme.closeButton.color,
    background: form.theme.closeButton.background.enabled
      ? form.theme.closeButton.background.color
      : "transparent",
    cursor: "pointer",
  };

  const formContainerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLDivElement>(null);
  const formContainerRect = formContainerRef.current?.getBoundingClientRect();

  // Keep close button on screen.
  useEffect(() => {
    if (!formContainerRef.current) return;
    const listener = () => {
      if (!closeButtonRef.current || !formContainerRef.current) return;

      const formContainerRect =
        formContainerRef.current.getBoundingClientRect();
      const windowRect = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      const cornerOfForm = { top: "16px", right: "16px" };
      const cornerOfWindow = {
        top: `${-formContainerRect.top + 16}px`,
        right: `${formContainerRect.right - windowRect.width + 16}px`,
      };

      closeButtonRef.current.style.right =
        formContainerRect.right <= windowRect.width
          ? cornerOfForm.right
          : cornerOfWindow.right;
      closeButtonRef.current.style.top =
        formContainerRect.top >= 0 ? cornerOfForm.top : cornerOfWindow.top;
    };
    const resizeObserver = new ResizeObserver(listener);
    resizeObserver.observe(formContainerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [formContainerRect]);

  const showImage =
    form.layout.image.style != "no" &&
    !(form.layout.image.hideOnMobile && showMobileView);
  const backgroundImage = form.layout.image.style == "background";
  const vertical =
    showMobileView ||
    form.layout.image.style === "top" ||
    form.layout.image.style === "bottom";
  const imageFirst =
    (showMobileView && form.layout.image.style !== "bottom") ||
    form.layout.image.style === "top" ||
    form.layout.image.style === "left";

  return (
    <Flex
      maxW="full"
      ref={formContainerRef}
      style={{
        ...size,
        backgroundImage: backgroundImage
          ? `url(${form.layout.image.url})`
          : "none",
        backgroundColor: "var(--redo-colors-gray-light-mode-300)",
        backgroundSize: "cover",
        padding: backgroundImage ? "50px" : "0px",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
      w="lg"
    >
      <Flex
        dir="column"
        gap="none"
        style={{
          ...size,
          ...style,
          background: form.layout.backgroundColor,
          width:
            backgroundImage && form.layout.type === "fullscreen"
              ? "60%"
              : "100%",
          height:
            backgroundImage && form.layout.type === "fullscreen"
              ? "auto"
              : size.height,
        }}
        w="full"
      >
        <Flex className={signupFormCss.closeButton} ref={closeButtonRef}>
          {onClose && (
            <RedoButtonCloseX
              onclick={onClose}
              onDarkBackground={false}
              size={ButtonSize.SMALL}
              style={closeButtonStyle}
            />
          )}
        </Flex>
        <Flex
          dir={vertical ? "column" : "row"}
          gap="none"
          height="full"
          w="full"
        >
          {showImage && !backgroundImage && imageFirst && (
            <SignupImage
              fullscreen={form.layout.type === "fullscreen"}
              imageFirst={imageFirst}
              rounding={form.theme.border.cornerRadiusPx + "px"}
              url={form.layout.image.url}
              vertical={vertical}
            />
          )}
          <Flex
            align="stretch"
            className={signupFormCss.formContent}
            dir="column"
            gap="none"
            justify="center"
            style={{
              width: vertical || !showImage || backgroundImage ? "100%" : "50%",
            }}
          >
            {children}
          </Flex>
          {showImage && !backgroundImage && !imageFirst && (
            <SignupImage
              fullscreen={form.layout.type === "fullscreen"}
              imageFirst={imageFirst}
              rounding={form.theme.border.cornerRadiusPx + "px"}
              url={form.layout.image.url}
              vertical={vertical}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const SignupImage = memo(function SignupImage({
  url,
  vertical,
  imageFirst,
  fullscreen,
  rounding,
}: {
  url: string;
  vertical: boolean;
  imageFirst: boolean;
  fullscreen: boolean;
  rounding?: string;
}) {
  const imageRounding = {
    borderRadius: `${imageFirst ? rounding : "0"} ${
      (imageFirst && vertical) || (!imageFirst && !vertical) ? rounding : "0"
    } ${!imageFirst ? rounding : "0"} ${(!imageFirst && vertical) || (imageFirst && !vertical) ? rounding : "0"}`,
  };

  return (
    <Flex
      style={{
        maxHeight: vertical && !fullscreen ? "150px" : "100%",
        width: vertical ? "100%" : "50%",
        overflow: "hidden",
      }}
    >
      {url && (
        <img
          className={signupFormCss.imageSection}
          src={url}
          style={{ objectFit: "cover", ...imageRounding }}
        />
      )}

      {!url && (
        <Flex
          align="center"
          className={signupFormCss.imageSectionPlaceholder}
          dir="column"
          justify="center"
          style={imageRounding}
        >
          <ImageIcon />
          Add image
        </Flex>
      )}
    </Flex>
  );
});
